/**
 * Codemax ARMS, spoken directly by the browser.
 *
 * Authorization Code with PKCE, which for a public client is the whole protocol: no
 * client secret exists, and the code is useless without the verifier that never left
 * this tab.
 *
 * These functions are deliberately stateless - they take a configuration and return a
 * value. Where the tokens live is `session.ts`, and the answer there is memory, per the
 * ARMS guidance to hold tokens in memory and not on disk.
 */

export interface ArmsConfig {
  readonly issuer: string
  readonly clientId: string
  readonly scopes: string
  readonly audience: string
}

export interface ArmsTokens {
  readonly accessToken: string
  readonly refreshToken: string | undefined
  readonly idToken: string | undefined
  readonly expiresAt: number
}

/** Survives the redirect and nothing more. Deleted the moment the code comes back. */
const PENDING_KEY = 'quotehero.arms.pending'

interface Pending {
  readonly verifier: string
  readonly state: string
  readonly nonce: string
  readonly returnTo: string
}

export function authorizeEndpoint(config: ArmsConfig): string {
  return `${config.issuer}connect/authorize`
}

export function tokenEndpoint(config: ArmsConfig): string {
  return `${config.issuer}connect/token`
}

export function logoutEndpoint(config: ArmsConfig): string {
  return `${config.issuer}connect/logout`
}

export function redirectUri(): string {
  return `${window.location.origin}/auth/callback`
}

export function postLogoutUri(): string {
  return `${window.location.origin}/signed-out`
}

/**
 * The PKCE pair. The verifier is 32 bytes of CSPRNG output; ARMS rejects `plain`, so
 * the challenge is always S256.
 */
export async function createPkce(): Promise<{ verifier: string; challenge: string }> {
  const verifier = randomString(32)
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return { verifier, challenge: base64Url(new Uint8Array(digest)) }
}

export function randomString(bytes: number): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(bytes)))
}

export function base64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Builds the authorize URL.
 *
 * `prompt=none` is what makes a page reload silent: ARMS' session cookie is first-party
 * on its own domain, and a top-level navigation carries it. The iframe version of this
 * trick is what Safari blocks, and this is not that.
 */
export function authorizeUrl(
  config: ArmsConfig,
  options: { challenge: string; state: string; nonce: string; silent: boolean },
): string {
  const query = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: redirectUri(),
    scope: config.scopes,
    state: options.state,
    nonce: options.nonce,
    code_challenge: options.challenge,
    code_challenge_method: 'S256',
  })

  if (options.silent) query.set('prompt', 'none')

  return `${authorizeEndpoint(config)}?${query.toString()}`
}

export function logoutUrl(config: ArmsConfig, idToken: string | undefined): string {
  const query = new URLSearchParams({ post_logout_redirect_uri: postLogoutUri() })

  // Without the hint ARMS asks "are you sure?", which is a strange thing to meet after
  // pressing Sign out.
  if (idToken) query.set('id_token_hint', idToken)

  return `${logoutEndpoint(config)}?${query.toString()}`
}

export function rememberPending(pending: Pending): void {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending))
  } catch {
    // Private browsing with storage disabled. The redirect will come back with a code
    // we cannot complete, and `takePending` says so by returning nothing.
  }
}

export function takePending(): Pending | undefined {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY)
    sessionStorage.removeItem(PENDING_KEY)
    return raw ? (JSON.parse(raw) as Pending) : undefined
  } catch {
    return undefined
  }
}

export class ArmsError extends Error {
  readonly code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'ArmsError'
    this.code = code
  }

  /**
   * ARMS answers a silent attempt with this when nobody is signed in. It is the normal
   * outcome of a first visit, not a fault.
   */
  get needsInteraction(): boolean {
    return this.code === 'login_required' || this.code === 'interaction_required'
      || this.code === 'consent_required' || this.code === 'account_selection_required'
  }
}

export async function exchangeCode(
  config: ArmsConfig,
  code: string,
  verifier: string,
): Promise<ArmsTokens> {
  return post(config, {
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  })
}

export async function exchangeRefreshToken(
  config: ArmsConfig,
  refreshToken: string,
): Promise<ArmsTokens> {
  return post(config, {
    grant_type: 'refresh_token',
    client_id: config.clientId,
    refresh_token: refreshToken,
  })
}

async function post(config: ArmsConfig, body: Record<string, string>): Promise<ArmsTokens> {
  const response = await fetch(tokenEndpoint(config), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
    body: new URLSearchParams(body).toString(),
  })

  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>

  if (!response.ok) {
    throw new ArmsError(
      typeof payload.error === 'string' ? payload.error : 'invalid_request',
      typeof payload.error_description === 'string' ? payload.error_description : 'Codemax sign-in failed.',
    )
  }

  const expiresIn = typeof payload.expires_in === 'number' ? payload.expires_in : 900

  return {
    accessToken: String(payload.access_token),
    refreshToken: typeof payload.refresh_token === 'string' ? payload.refresh_token : undefined,
    idToken: typeof payload.id_token === 'string' ? payload.id_token : undefined,
    expiresAt: Date.now() + expiresIn * 1000,
  }
}

/**
 * Reads a JWT's payload without verifying it.
 *
 * Deliberately not a verification: this token came straight from the token endpoint over
 * TLS in response to a code only this tab could redeem, so there is no untrusted party in
 * between whose signature we would be checking. The API is where signatures are verified,
 * against ARMS' published keys, because that is where a token arrives from somewhere else.
 */
export function readClaims(jwt: string | undefined): Record<string, unknown> {
  if (!jwt) return {}

  const [, payload] = jwt.split('.')
  if (!payload) return {}

  try {
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>
  } catch {
    return {}
  }
}
